import React from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { useAppTakeoverCart } from 'components/app-takeover/use-app-takeover-cart';
import { PaymentProcessLoading } from 'components/payment-process-loading';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { ClickEventComponentNames } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useServiceModeLive } from 'state/service-mode';
import { useSmartAlertsContext } from 'state/smart-alert';
import { spacing } from 'styles/constants';
import { logger } from 'utils/logger';
import { TraceName, startTrace } from 'utils/performance/trace';

import { useCheckoutState } from '../../checkout-provider';
import { useCheckoutLoadingState } from '../../hooks';
import { useCheckoutSubmit } from '../../hooks/use-checkout-submit';
import { useCheckoutValidation } from '../../hooks/use-checkout-validation';

export const PlaceOrderButton = () => {
  const {
    submitOrder,
    ErrorDialog,
    wasCommitOrderCalled,
    commitOrderSuccess,
    commitOrderError,
  } = useCheckoutSubmit();
  const { validate } = useCheckoutValidation();
  const checkoutErrors = validate();
  const [, setCheckoutState] = useCheckoutState();
  const { isStoreOpenAndAvailable, store } = useStoreContext();
  const serviceMode = useServiceModeLive();
  const { currentStoreDate } = useSmartAlertsContext();
  const { isStoreAvailable } = useStoreAvailabilityFns();
  const {
    isPlaceOrderLoading,
    isLoadingPaymentMethods,
    isOrderPriceLoading,
  } = useCheckoutLoadingState();

  const { hasPaymentProcessingTakeover } = useAppTakeoverCart();
  const hasFooterErrors = checkoutErrors.some(error => error.placement === 'footer');
  const isStoreUnavailable =
    !isStoreOpenAndAvailable ||
    !serviceMode ||
    !isStoreAvailable(store, serviceMode, currentStoreDate ?? store.localTime);

  const _rawShowPaymentProcessLoading =
    hasPaymentProcessingTakeover &&
    wasCommitOrderCalled &&
    !commitOrderSuccess &&
    !commitOrderError;

  // debounce until after any native payment drawers dismiss
  // these mess up the animation modal's presentation. 1s is long enough.
  const showPaymentProcessLoading = useDebouncedValue(_rawShowPaymentProcessLoading, 1000);

  const handlePress = () => {
    if (isStoreUnavailable) {
      return;
    }

    const shouldSubmitOrder = checkoutErrors.length === 0;
    if (shouldSubmitOrder) {
      startTrace(TraceName.PLACE_ORDER_TO_RECEIPT);
      submitOrder();
    }
    const dialogErrors = checkoutErrors.filter(error => error.placement !== 'footer');
    const shouldOpenErrorModal = dialogErrors.length > 0;
    if (shouldOpenErrorModal) {
      setCheckoutState(prev => ({ ...prev, errorModalOpen: true }));
    }

    if (!shouldSubmitOrder && !shouldOpenErrorModal) {
      logger.error({
        isPaymentLog: true,
        message: '[PlaceOrderButton] Button pressed without submit or error modal action',
        checkoutErrors,
      });
    }
  };

  let buttonText = t('placeSecureOrder', { secureOrderIcon: '' });

  if (isPlaceOrderLoading && !isLoadingPaymentMethods && !isOrderPriceLoading) {
    buttonText = t('placingOrderCheckoutBtn');
  }

  return (
    <>
      {showPaymentProcessLoading && <PaymentProcessLoading />}
      <View style={styles.buttonContainer}>
        <ActionButton
          startIcon={<Icon variant="locked" />}
          onPress={handlePress}
          disabled={isPlaceOrderLoading || hasFooterErrors || isStoreUnavailable}
          size={ActionButtonSizes.LARGE}
          isLoading={isPlaceOrderLoading}
          fullWidth
          eventAttributes={{
            component: ClickEventComponentNames.BUTTON,
            name: 'Place Secure Order',
          }}
        >
          {buttonText}
        </ActionButton>
      </View>

      {/* make sure any payment processing modal is hidden before showing the error dialog or things can get stuck on ios */}
      {!showPaymentProcessLoading && <ErrorDialog />}
    </>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    width: '100%',
    paddingHorizontal: 16,
    marginTop: spacing.$4,
  },
});
